import { CommonModule, DatePipe } from '@angular/common';
import { Component, inject, Inject, OnInit, signal, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, FormsModule, NgModel, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { AlertDialogComponent } from '../../shared/components/alert-dialog/alert-dialog.component';
import { siteDataInput } from '../../../interfaces/labDataModel';
import { SiteDataService } from '../../../services/sitedata.service';
import { MatDatepickerModule } from "@angular/material/datepicker";
import { GetSiteDataCaptureDigestersResponse, SaveSDCaptureRequest, SaveSiteDataCaptureRequest, SDCEquipmentItem, SDCEquipmentSelection, SiteDigesterCaptureEquipmentData } from '../../../interfaces/sdcmodel';
import { cleanNum, formatDateOnly, numericInputLimit } from '../../shared/commonutils/helper';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatSelectModule } from "@angular/material/select";

@Component({
  selector: 'app-addedit-sitedatacapture',
  imports: [
    CommonModule, FormsModule, ReactiveFormsModule, MatCardModule, MatFormFieldModule,
    MatInputModule, MatButtonModule, MatTabsModule, MatDialogModule,
    MatIconModule, MatSlideToggleModule, MatDividerModule, MatSnackBarModule,
    MatDatepickerModule, DragDropModule,
    MatSelectModule
  ],
  templateUrl: './addedit-sitedatacapture.component.html',
  styleUrl: './addedit-sitedatacapture.component.scss'
})
export class AddeditSitedatacaptureComponent implements OnInit {

  @ViewChild('dcctrl') dcctrl!:NgModel;
  private datePipe = inject(DatePipe);
  attemptedSave = signal<boolean>(false);
  displayDate = '';
  dynamicTabs = signal<any[]>([]);
  reasonSum = 0;
  selectedTabIndex = signal(0);
  proformaFlag = signal<boolean>(false);  
  displayValue = signal<string>('');
  canShowSaveButton = signal<boolean>(false);
  previousDayGasProduction = signal<number | null>(null);
  equipmentGroups = signal<Record<string, SDCEquipmentItem[]>>({});
  filteredEquipmentGroups = signal<Record<string, SDCEquipmentItem[]>>({});
  selectedEquipment = signal<SDCEquipmentSelection[]>([]);
  readonly sequenceOptions = Array.from({ length: 100 }, (_, i) => i + 1);
  readonly EQUIPMENT_FIELD_MAP: { typeName: string; label: string }[] = [
    { typeName: 'AD',                label: 'AD Equipment' },
    { typeName: 'ORA',               label: 'ORA Equipment' },
    { typeName: 'Upgrader',          label: 'Upgrader' },
    { typeName: 'DigesterBiology',   label: 'Digester Biology' },
    { typeName: 'FeedstockQuantity', label: 'Feedstock Quantity' },
    { typeName: 'FeedstockQuality',  label: 'Feedstock Quality' },
    { typeName: 'DigestateManagement', label: 'Digestate Management' },
    { typeName: 'External',          label: 'External' },
  ];
  get totalSelected(): number {
    return this.selectedEquipment().length;
  }
  duplicateSequenceError = signal<number | null>(null);
  equipmentLabelMap = new Map<number, string>();
  text: string = ''
  sdcForm = new FormGroup({    
    isOraDowntime: new FormControl<boolean>(false),
    oraDowntimeHours: new FormControl<number | null>({ value: null, disabled: true, },[Validators.max(24)]),

    adEquipment: new FormControl<number | null>({ value: null, disabled: false }, [Validators.max(100)]),
    oraEquipment: new FormControl<number | null>({ value: null, disabled: false }, [Validators.max(100)]),
    upgrader: new FormControl<number | null>({ value: null, disabled: false }, [Validators.max(100)]),
    digesterBiology: new FormControl<number | null>({ value: null, disabled: false }, [Validators.max(100)]),
    feedstockQuantity: new FormControl<number | null>({ value: null, disabled: false }, [Validators.max(100)]),
    feedstockQuality: new FormControl<number | null>({ value: null, disabled: false }, [Validators.max(100)]),
    digestateManagement: new FormControl<number | null>({ value: null, disabled: false }, [Validators.max(100)]),
    external: new FormControl<number | null>({ value: null, disabled: false }, [Validators.max(100)]),
    
    notes: new FormControl<string | null>(''),
    dynamicData: new FormArray<FormGroup>([])
  }, { validators: [this.validateDowntimeSum] });

  constructor(
    public dialogRef: MatDialogRef<AddeditSitedatacaptureComponent>,
    @Inject(MAT_DIALOG_DATA) public data: siteDataInput,
    private dialog: MatDialog,
    private service: SiteDataService
  ) {
    console.log('Dialog data received:', data);
    if(data.isEdit){
      this.text = `(${data.id})`;
    }
    data.userName = data.userName.split('@')[0];
    this.displayDate = this.datePipe.transform(data.dateCaptured || new Date(), 'MM/dd/yyyy') || '';
    this.canShowSaveButton.set(data.canShowSaveButton);
  }

  ngOnInit() {
    //this.loadEquipmentData();
    this.fetchSiteConfiguration();
    this.setupSumListener();
    this.setupDowntimeToggleListener();
  }

  loadEquipmentData(){
    this.service.getsdcEquipData().subscribe((result : SDCEquipmentItem[]) =>{
      const grouped = result.reduce((acc, item) => {
        if (!acc[item.typeName]) acc[item.typeName] = [];
        acc[item.typeName].push(item);
        this.equipmentLabelMap.set(item.id, item.equipmentValue);
        return acc;
      }, {} as Record<string, SDCEquipmentItem[]>);
      this.equipmentGroups.set(grouped);
      this.filteredEquipmentGroups.set(grouped);
    });
  }

  onEquipmentPanelToggle(opened: boolean, typeName: string): void {
    if (!opened) {
      this.filteredEquipmentGroups.update(current => ({
        ...current,
        [typeName]: [...(this.equipmentGroups()[typeName] || [])]
      }));
    }
  }

  onEquipmentSelectionChange(typeName: string, selectedIds: number[]): void {
    const current = this.selectedEquipment();

    const removedItems = current.filter(s => s.typeName === typeName && !selectedIds.includes(s.equipmentId));

    let kept = current.filter(s => s.typeName !== typeName || selectedIds.includes(s.equipmentId));    

    const newItems: SDCEquipmentSelection[] = selectedIds
      .filter(id => !kept.some(s => s.equipmentId === id))
      .map(id => ({
          id: 0,
          equipmentId: id,
          typeName,
          minutes:     null,
          comments: "",
          impactId: null
      }));

    this.selectedEquipment.set([...kept, ...newItems]);
  }

  getSelectedIdsForType(typeName: string): number[] {
    return this.selectedEquipment()
      .filter(s => s.typeName === typeName)
      .map(s => s.equipmentId);
  }
  
  onMinutesInput(equipmentId: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    let raw = input.value.replace(/[^0-9]/g, '');
    
    if (raw.length > 4) raw = raw.substring(0, 4);

    input.value = raw;
    const num = raw === '' ? null : parseInt(raw, 10);
    this.selectedEquipment.update(items =>
      items.map(item => item.equipmentId === equipmentId ? { ...item, minutes: num } : item)
    );
  }

  setupSumListener() {
    const reasonFields = [
      'adEquipment', 'oraEquipment', 'upgrader', 'digesterBiology',
      'feedstockQuantity', 'feedstockQuality', 'digestateManagement', 'external'
    ];
    
    this.sdcForm.valueChanges.subscribe(val => {
      this.reasonSum = reasonFields.reduce((sum, field) => sum + (Number((val as any)[field]) || 0), 0);
    });
  }

  setupDowntimeToggleListener() {
    this.sdcForm.get('isOraDowntime')?.valueChanges.subscribe(isActive => {
      const hoursCtrl = this.sdcForm.get('oraDowntimeHours');
      if (isActive) {
        hoursCtrl?.enable();
        hoursCtrl?.setValidators([Validators.required, Validators.min(1), Validators.max(24)]);
      } else {
        hoursCtrl?.clearValidators();
        hoursCtrl?.disable();
        hoursCtrl?.setValue(null);
      }
      hoursCtrl?.updateValueAndValidity();
    });
  }

  validateDowntimeSum(control: AbstractControl): ValidationErrors | null {
    const isDowntime = control.get('isOraDowntime')?.value;
    if (!isDowntime) return null;

    const fields = ['adEquipment', 'oraEquipment', 'upgrader', 'digesterBiology', 'feedstockQuantity', 'feedstockQuality', 'digestateManagement', 'external'];
    const sum = fields.reduce((acc, f) => acc + (Number(control.get(f)?.value) || 0), 0);
    return sum === 100 ? null : { sumNot100: true };
  }

  filterequipment(event: Event, tyName: string): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    const allItems   = this.equipmentGroups()[tyName] || [];

    const filtered = !searchTerm
      ? [...allItems]
      : allItems.filter(item => item.equipmentValue.toLowerCase().includes(searchTerm));
      
    this.filteredEquipmentGroups.update(current => ({ ...current, [tyName]: filtered}));
  }

  get dynamicData(): FormArray {
    return this.sdcForm.get('dynamicData') as FormArray;
  }

  getDynamicTabGroup(index: number): FormGroup {
    return this.dynamicData.at(index) as FormGroup;
  }

  getGroup(index: number): FormGroup {
    return this.dynamicData.at(index) as FormGroup;
  }

  onlyIntegers(event: KeyboardEvent) {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      event.preventDefault();
    }
  }

  fetchSiteConfiguration(clearExisting = false) {
    var ipDate = formatDateOnly(this.data.dateCaptured);
    if(clearExisting){
      this.service.getsdcDynTabs(this.data.siteId?.toString() || null, ipDate ?? '').subscribe(tabsInfo => {
        console.log("tabs info", tabsInfo);
        this.previousDayGasProduction.set(
          tabsInfo.length > 0 ? tabsInfo[0].previousDayGasProduction : null
        );
        this.previousDayGasProduction.set(tabsInfo.length > 0 ? tabsInfo[0].previousDayGasProduction : null);
        const proforma = tabsInfo.length > 0  ? tabsInfo[0].proformaFlag === true : false;
        this.proformaFlag.set(proforma);
        this.updateProformaControls(proforma);
      });
      return;
    }
    this.service.getsdcDynTabs(this.data.siteId?.toString() || null, ipDate ?? '').subscribe(tabsInfo => {
      console.log('Fetched site configuration:', tabsInfo);
      const tabs = tabsInfo;
      this.dynamicTabs.set(tabs.map(t => ({ tabName: t.digesterName, id: t.digesterId })));
      tabs.forEach(tab => {
        this.dynamicData.push(this.createDynamicTabGroup(tab));
        this.proformaFlag.set(tab.proformaFlag);
      });
      this.previousDayGasProduction.set(tabsInfo.length > 0 ? tabsInfo[0].previousDayGasProduction : null);
      const proforma = tabsInfo.length > 0  ? tabsInfo[0].proformaFlag === true : false;
      this.proformaFlag.set(proforma);
      this.updateProformaControls(proforma);
      if (this.data.isEdit) {
        this.fetchAndPatchData();
      }
    });
  }

  private updateProformaControls(proformaFlag: boolean): void {
    // const fields = [
    //   'adEquipment',
    //   'oraEquipment',
    //   'upgrader',
    //   'digesterBiology',
    //   'feedstockQuantity',
    //   'feedstockQuality',
    //   'digestateManagement',
    //   'external'
    // ];
  
    // fields.forEach(field => {
    //   const ctrl = this.sdcForm.get(field);
  
    //   if (proformaFlag) {
    //     ctrl?.enable({ emitEvent: false });
    //   } else {
    //     ctrl?.setValue(null, { emitEvent: false });
    //     ctrl?.disable({ emitEvent: false });
    //   }
  
    //   ctrl?.updateValueAndValidity({ emitEvent: false });
    // });
  }

  onDateCapturedChange(date: Date | null): void {
    if (this.data.isEdit || !date) {
      return;
    }
  
    this.data.dateCaptured = date;
    this.fetchSiteConfiguration(true);
  }

  createDynamicTabGroup(tab: GetSiteDataCaptureDigestersResponse): FormGroup {
    return new FormGroup({
      id: new FormControl(0),
      digesterId: new FormControl(tab.digesterId),
      digesterpH: new FormControl(null, [Validators.max(9.9)]),
      digesterORP: new FormControl(null),
      hydrolyzerpH: new FormControl(null, Validators.required),
      digesterpHTitrator: new FormControl(null, Validators.required),
      numberofTitrations: new FormControl(null),
      tic1: new FormControl(null),
      tic2: new FormControl(null),
      tic3: new FormControl(null),
      averageOfTIC: new FormControl({ value: null, disabled: true }),
      tic: new FormControl({ value: null, disabled: true }),
      vfa: new FormControl({ value: null, disabled: true }),
      ratio: new FormControl({ value: null, disabled: true }),
      sphp : new FormControl(false),
      notes: new FormControl('', Validators.maxLength(250)),
      vfa1: new FormControl(null),
      vfa2: new FormControl(null),
      vfa3: new FormControl(null),
      averageOfVFA: new FormControl({ value: null, disabled: true }),
      handheldTemperatureforAD: new FormControl(null),
      handheldCH4Percentage: new FormControl(null),
      handheldCO2Percentage: new FormControl(null),
      handheldO2Percentage: new FormControl(null),
      handheldH2Sppm: new FormControl(null),
      yesterdayFlareVolumeMSCF: new FormControl(null),
      oraslurryTS: new FormControl(null),
      ht2tS: new FormControl(null),
      oraslurryTS_2: new FormControl(null),
      ht2tS_2: new FormControl(null),
      aDTS: new FormControl(null),
      adChlorides: new FormControl(null),
      hT2Chlorides: new FormControl(null),
    });
  }

  fetchAndPatchData() {
    const siteId = this.data.siteId?.toString() || null;
    const dateCaptured = this.data.dateCaptured || null;
    this.service.getsdcTabsDataById(this.data.id).subscribe(response => {
      console.log('Fetched existing site data for editing:', response);
      this.sdcForm.patchValue({
        isOraDowntime: false,
        oraDowntimeHours: 0,
        notes: ''
      });
    
      response.siteDigesterData.forEach((dataEntry: any) => {
        const tabIndex = this.dynamicTabs().findIndex(tab => tab.id === dataEntry.digesterId);
        if (tabIndex !== -1) {
          const formGroup = this.getDynamicTabGroup(tabIndex);
          formGroup.patchValue({
            id: dataEntry.id,
            digesterId: dataEntry.digesterId,
            digesterpH: dataEntry.digesterpH,
            digesterORP: dataEntry.digesterORP,
            hydrolyzerpH: dataEntry.hydrolyzerpH,
            digesterpHTitrator: dataEntry.digesterpHTitrator,
            numberofTitrations: dataEntry.numberofTitrations,
            tic1: dataEntry.tiC1,
            tic2: dataEntry.tiC2,
            tic3: dataEntry.tiC3,
            averageOfTIC: dataEntry.averageOfTIC,
            tic: dataEntry.tic,
            ratio: dataEntry.ratio,
            sphp: dataEntry.sphp,
            notes: dataEntry.notes,
            vfa1: dataEntry.vfA1,
            vfa2: dataEntry.vfA2,
            vfa3: dataEntry.vfA3,
            averageOfVFA: dataEntry.averageOfVFA,
            vfa: dataEntry.vfa,
            handheldTemperatureforAD: dataEntry.handheldTemperatureforAD,
            handheldCH4Percentage: dataEntry.handheldCH4Percentage,
            handheldCO2Percentage: dataEntry.handheldCO2Percentage,
            handheldO2Percentage: dataEntry.handheldO2Percentage,
            handheldH2Sppm: dataEntry.handheldH2Sppm,            
            yesterdayFlareVolumeMSCF: dataEntry.yesterdayFlareVolumeMSCF,
            oraslurryTS: dataEntry.oraSlurryTS,
            ht2tS: dataEntry.hT2TS,
            oraslurryTS_2: dataEntry.oraSlurryTS_2,
            ht2tS_2: dataEntry.hT2TS_2,
            aDTS: dataEntry.adts,
            adChlorides: dataEntry.adChlorides,
            hT2Chlorides: dataEntry.hT2Chlorides
          });
        }
      });
    });
  }

  private getTypeNameForEquipmentId(equipmentId: number): string {
    const groups = this.equipmentGroups();
    for (const typeName of Object.keys(groups)) {
      if (groups[typeName].some(e => e.id === equipmentId)) return typeName;
    }
    return '';
  }

  onSave() {
    this.attemptedSave.set(true);
    this.dcctrl.control?.markAsTouched();
    if(this.dcctrl.invalid) {
      return;
    }    
    this.sdcForm.markAllAsTouched();
 
    if (this.sdcForm.invalid) { 
      const dynamicDataArray = this.sdcForm.get('dynamicData') as FormArray;
      for (let i = 0; i < dynamicDataArray.length; i++) {
        if (dynamicDataArray.at(i).invalid) {
          this.selectedTabIndex.set(i + 1);
          return;
        }
      }
    }
    
    const raw = this.sdcForm.getRawValue();
    const nowISO = new Date().toISOString();
    let finalDateCaptured: string;
    if (this.data.dateCaptured) {
      const d = new Date(this.data.dateCaptured);
      finalDateCaptured = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString();
    } else {
      finalDateCaptured = nowISO;
    }

    const payload: SaveSiteDataCaptureRequest = {
      siteDigesterGeneralData: {
        id: this.data.id || 0,
        siteId: parseInt(this.data.siteId.split('-')[1]),
        siteName: this.data.siteName || '',
        dateCaptured: finalDateCaptured,
        isOraDowntime: raw.isOraDowntime || false,
        oraDowntimeHours: raw.oraDowntimeHours || 0,
        //adEquipmentDowntime: 0,
        // oraEquipmentDowntime: 0,
        // upgraderDowntime: 0,
        // digesterBiologyHealth: 0,
        // feedstockQuantity: 0,
        // feedstockQuality: 0,
        // digestateManagement: 0,
        // external: 0,
        proformaFlag: this.proformaFlag(),
        previousDayGasProduction: this.previousDayGasProduction() || 0,
        notes: raw.notes || '',
        createdBy: this.data.userName,
        createdByName: this.data.userName,
        createdDate: nowISO,
        lastModifiedBy: this.data.userName,
        modifiedDate: nowISO,
        ad: '0',
        ora: '0',
        upgrader: '0',
        digesterBiology: '0',
        feedstockQuantity: '0',
        feedstockQuality: '0',
        digestateManagement: '0',
        external: '0',
        tic: null,
        vfa: null,
        ph: null,
        orp: null
      },
      siteDigesterData: raw.dynamicData.map((d: any) => ({
        ...d,
        id: d.id ?? 0,
        digesterpH: cleanNum(d.digesterpH),
        digesterORP: cleanNum(d.digesterORP),
        hydrolyzerpH: cleanNum(d.hydrolyzerpH),
        digesterpHTitrator: cleanNum(d.digesterpHTitrator),
        numberofTitrations: cleanNum(d.numberofTitrations),
        tic1: cleanNum(d.tic1),
        tic2: cleanNum(d.tic2),
        tic3: cleanNum(d.tic3),
        averageOfTIC: cleanNum(d.averageOfTIC),
        tic: cleanNum(d.tic),
        vfa1: cleanNum(d.vfa1),
        vfa2: cleanNum(d.vfa2),
        vfa3: cleanNum(d.vfa3),
        averageOfVFA: cleanNum(d.averageOfVFA),
        vfa: cleanNum(d.vfa),
        ratio: cleanNum(d.ratio),
        // This was the specific field mentioned in your error log:
        handheldTemperatureforAD: cleanNum(d.handheldTemperatureforAD),
        handheldCH4Percentage: cleanNum(d.handheldCH4Percentage),
        handheldCO2Percentage: cleanNum(d.handheldCO2Percentage),
        handheldO2Percentage: cleanNum(d.handheldO2Percentage),
        handheldH2Sppm: cleanNum(d.handheldH2Sppm),
        yesterdayFlareVolumeMSCF: cleanNum(d.yesterdayFlareVolumeMSCF),
        oraslurryTS: cleanNum(d.oraslurryTS),
        ht2tS: cleanNum(d.ht2tS),
        oraslurryTS_2: cleanNum(d.oraslurryTS_2),
        ht2tS_2: cleanNum(d.ht2tS_2),
        aDTS: cleanNum(d.aDTS),
        siteDigesterGeneralDataId: this.data.id || 0,
        adChlorides: cleanNum(d.adChlorides),
        hT2Chlorides: cleanNum(d.hT2Chlorides)
      })),
      equipmentData:[],
      proformaFlag: false
    };

    console.log('Final Payload:', payload);
    this.service.saveSDCData(payload).subscribe(success => {
      console.log('Save operation success status:', success);
      if (success) {
        this.dialogRef.close(true);
      }
    });
  }

  getEquipmentLabel(equipmentId: number): string {
    return this.equipmentLabelMap.get(equipmentId) ?? `Item {equipmentId}`;
  }

  onCancel() {
    this.dialogRef.close(false);
  }

  capPHValue(index: number, controlName: string) {
    const group = this.getGroup(index);
    let val = group.get(controlName)?.value;
    if (val !== null && val > 9.9) {
      group.get(controlName)?.setValue(9.9);
    }
  }
  
  applyLimits(index: number, controlName: string, max: number, decimals: number) {
    const group = this.getGroup(index);
    let val = group.get(controlName)?.value;
    if (val !== null && !isNaN(val)) {
      if (val > max) val = max;
      group.get(controlName)?.setValue(parseFloat(val.toFixed(decimals)));
    }
  }

  roundOffDynamic(event: Event,index: number, controlName: string, maxValue: number, maxDecimals: number,
    allowNegative: boolean = false
  ): void {
    const input = event.target as HTMLInputElement;
  
    let val = numericInputLimit(event, maxValue, maxDecimals, allowNegative);
  
    this.displayValue.set(val);
    input.value = val;
  
    const ctrl = this.getDynamicTabGroup(index).get(controlName);
  
    const isValueEmpty = val === '' || val === '.';
  
    if (isValueEmpty) {
      ctrl?.setValue(null, { emitEvent: false });
    } else if (val.endsWith('.')) {
      ctrl?.setErrors({ incompleteDecimal: true });
    } else {
      ctrl?.setValue(parseFloat(val), { emitEvent: false });
    }
    requestAnimationFrame(() => {
      if (document.activeElement === input) {
          input.value = val;
      }
    });
  }

  roundOff(event: Event, maxValue: number, maxDecimals: number, type: number, controlName: string): void {
    const input = event.target as HTMLInputElement;
    let val = input.value;
    
    val = numericInputLimit(event, maxValue, maxDecimals);
    
    this.displayValue.set(val);
    input.value = val;

    // 6. Parsing for the backing variables
    const isValueEmpty = val === '' || val === '.';
    if (isValueEmpty) {
      this.sdcForm.get(controlName)?.setValue(null, { emitEvent: false });
    } else if (val.endsWith('.')) {
      this.sdcForm.get(controlName)?.setErrors({ 'incompleteDecimal': true });
    } else {
      const parsedValue = parseFloat(val);
      this.sdcForm.get(controlName)?.setValue(parsedValue, { emitEvent: false });
    }
    requestAnimationFrame(() => {
      if (document.activeElement === input) {
          input.value = val;
      }
    });
  }

  limitToInteger(index: number, controlName: string) {
    const group = this.getGroup(index);
    let val = group.get(controlName)?.value;
    if (val !== null) {
      let intVal = Math.floor(val);
      if (intVal > 99999) intVal = 99999;
      group.get(controlName)?.setValue(intVal, { emitEvent: false });
    }
  }

  roundOffDynamicAndCalculateTIC(
    event: Event,
    index: number,
    controlName: string,
    maxValue: number,
    maxDecimals: number
  ): void {
    this.roundOffDynamic(event, index, controlName, maxValue, maxDecimals);
    this.calculateTIC(index);
  }

  roundOffDynamicAndCalculateVFA(
    event: Event,
    index: number,
    controlName: string,
    maxValue: number,
    maxDecimals: number
  ): void {
    this.roundOffDynamic(event, index, controlName, maxValue, maxDecimals);
    this.calculateVFA(index);
  }

  // Logic from OnTICChange
  calculateTIC(index: number) {
    const g = this.getGroup(index);
    const t1 = g.get('tic1')?.value || 0;
    const t2 = g.get('tic2')?.value || 0;
    const t3 = g.get('tic3')?.value || 0;

    const avgTotal = (t1 + t2 + t3) / 3;
    const ticNo = avgTotal * 4 * 250;

    g.get('averageOfTIC')?.setValue(parseFloat(avgTotal.toFixed(3)));
    g.get('tic')?.setValue(parseFloat(ticNo.toFixed(3)));
    this.calculateRatio(index);
  }

  // Logic from OnVFAChange
  calculateVFA(index: number) {
    const g = this.getGroup(index);
    const v1 = g.get('vfa1')?.value || 0;
    const v2 = g.get('vfa2')?.value || 0;
    const v3 = g.get('vfa3')?.value || 0;

    const avgTotal = (v1 + v2 + v3) / 3;
    const vfaNo = (avgTotal * 4 * 1.66 - 0.15) * 500;

    g.get('averageOfVFA')?.setValue(parseFloat(avgTotal.toFixed(3)));
    g.get('vfa')?.setValue(parseFloat(vfaNo.toFixed(3)));
    this.calculateRatio(index);
  }

  calculateRatio(index: number) {
    const g = this.getGroup(index);
    const tic = g.get('tic')?.value || 0;
    const vfa = g.get('vfa')?.value || 0;
    if (tic !== 0) {
      g.get('ratio')?.setValue(parseFloat((vfa / tic).toFixed(3)));
    } else {
      g.get('ratio')?.setValue(0);
    }
  }

  onKeydown(event: KeyboardEvent) {
    if(event.ctrlKey && event.metaKey){return;} 
    if(!/[0-9\/]/.test(event.key) && event.key.length === 1){
      event.preventDefault();
    }
  }
  dateformat(event: Event){
    const input = event.target as HTMLInputElement;
    let value = input.value;

    if(!value) return;
    const parts = value.split('/');
    if(parts.length !== 3) return;

    let [month, day, year] = parts.map(part => part.padStart(2, '0'));
    if(month.length === 1)  month = '0' + month;
    if(day.length === 1)  day = '0' + day;
    if(year.length === 2) year = '20' + year;
    input.value = `${month}/${day}/${year}`;
  }
}
